/**
 * AWS Amplify configuration for connecting to existing AppSync and Cognito resources.
 *
 * Amplify is configured **lazily** (dynamic import) so the heavy aws-amplify SDK
 * is not pulled into the entry chunk. Logged-out visitors on the landing page
 * never need it (see AuthContext's optimistic session gate), so it only loads
 * once there's a session to verify or a data/auth call to make.
 *
 * `ensureAmplifyConfigured()` is idempotent and returns a cached promise, so any
 * number of call sites can `await` it cheaply before using Amplify.
 *
 * Requirements: 2.3, 2.6
 */

export function buildConfig() {
  return {
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
        userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
        signUpVerificationMethod: 'code' as const,
        loginWith: {
          email: true,
        },
      },
    },
    API: {
      GraphQL: {
        endpoint: import.meta.env.VITE_APPSYNC_ENDPOINT || '',
        region: import.meta.env.VITE_AWS_REGION || 'ap-southeast-2',
        defaultAuthMode: 'userPool' as const,
        apiKey: import.meta.env.VITE_APPSYNC_API_KEY || '',
      },
    },
  };
}

let configurePromise: Promise<void> | null = null;
let configured = false;

/** Minimal shape of the Amplify singleton — avoids importing the SDK's types here. */
interface AmplifyLike {
  configure: (config: ReturnType<typeof buildConfig>) => void;
}

/**
 * Apply the config to an already-loaded Amplify singleton. Synchronous and
 * idempotent.
 *
 * This exists so `lib/api-client.ts` — which has the SDK statically, because
 * every GraphQL module does — can configure Amplify at module-evaluation time,
 * *before* it creates the client. `Amplify.configure()` itself is synchronous;
 * only fetching the SDK is not, and by that point it is already fetched.
 */
export function configureAmplifyWith(amplify: AmplifyLike): void {
  if (configured) return;

  const config = buildConfig();
  if (!config.Auth.Cognito.userPoolId || !config.API.GraphQL.endpoint) {
    console.warn(
      'AWS Amplify not configured. Set VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID, and VITE_APPSYNC_ENDPOINT environment variables.'
    );
    return;
  }

  amplify.configure(config);
  configured = true;
  // Anyone already awaiting ensureAmplifyConfigured() is satisfied by this.
  configurePromise = Promise.resolve();
}

/**
 * Configure Amplify exactly once (lazily importing the SDK). Subsequent calls
 * return the same cached promise. Safe to `await` from any auth/api path.
 */
export function ensureAmplifyConfigured(): Promise<void> {
  if (!configurePromise) {
    configurePromise = (async () => {
      // Check the environment before pulling the SDK in: a build without these
      // values can never configure, and downloading ~500kB to then warn about
      // it helps nobody.
      if (!isAmplifyConfigured()) {
        console.warn(
          'AWS Amplify not configured. Set VITE_COGNITO_USER_POOL_ID, VITE_COGNITO_CLIENT_ID, and VITE_APPSYNC_ENDPOINT environment variables.'
        );
        return;
      }
      const { Amplify } = await import('aws-amplify');
      configureAmplifyWith(Amplify);
    })();
  }
  return configurePromise;
}

/** Test seam: forget that Amplify was configured. */
export function _resetAmplifyConfigForTests(): void {
  configured = false;
  configurePromise = null;
}

/**
 * Check if Amplify is properly configured (env presence only — no SDK needed).
 */
export function isAmplifyConfigured(): boolean {
  return !!(
    import.meta.env.VITE_COGNITO_USER_POOL_ID &&
    import.meta.env.VITE_COGNITO_CLIENT_ID &&
    import.meta.env.VITE_APPSYNC_ENDPOINT
  );
}

/**
 * Cheap, synchronous check for a stored Cognito session — scans localStorage for
 * Amplify's access-token key without loading the SDK. Lets the app render the
 * logged-out landing page immediately (and skip loading Amplify) when there's
 * clearly no session, while still verifying properly when one might exist.
 */
export function hasStoredCognitoSession(): boolean {
  try {
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    if (!clientId) return false;
    const prefix = `CognitoIdentityServiceProvider.${clientId}`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix) && key.endsWith('.accessToken')) {
        return true;
      }
    }
    return false;
  } catch {
    // localStorage unavailable (private mode/SSR) — assume a session may exist
    // so we fall back to the full (correct) Amplify check rather than skipping it.
    return true;
  }
}
